import { useEffect, useRef, useState } from "react";
import type { ItemKind } from "@csf/shared";
import { useCatalogSearch, type CatalogItem } from "../api/catalog.js";

interface Props<K extends ItemKind> {
  kind: K;
  onPick: (item: CatalogItem<K>) => void;
  placeholder: string;
}

// Дебаунс — простой setTimeout. 250 мс достаточно для UX и щадит API.
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function CatalogSearch<K extends ItemKind>({ kind, onPick, placeholder }: Props<K>) {
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 250);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCatalogSearch(kind, debounced, open);

  // Закрываем результаты по клику вне.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full rounded-xl bg-bg-secondary px-4 py-2 outline-none placeholder:text-hint"
      />
      {open && debounced.trim().length >= 2 && (
        <div className="absolute left-0 right-0 z-10 mt-1 max-h-72 overflow-y-auto rounded-xl bg-bg-secondary shadow-lg">
          {search.isLoading && (
            <div className="px-4 py-2 text-sm text-hint">Поиск...</div>
          )}
          {search.isError && (
            <div className="px-4 py-2 text-sm text-destructive">Ошибка поиска</div>
          )}
          {search.isSuccess && search.data && search.data.length === 0 && (
            <div className="px-4 py-2 text-sm text-hint">Ничего не найдено</div>
          )}
          {search.data?.map((item, idx) => (
            <button
              key={`${item.displayName}-${idx}`}
              type="button"
              onClick={() => {
                onPick(item);
                setQuery(item.displayName);
                setOpen(false);
              }}
              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-bg"
            >
              {item.iconUrl && (
                <img
                  src={item.iconUrl}
                  alt=""
                  className="h-8 w-8 flex-shrink-0 rounded object-contain"
                />
              )}
              <span
                className="flex-1 truncate"
                style={item.rarityColor ? { color: `#${item.rarityColor}` } : {}}
              >
                {item.displayName}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
